import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarDays, AlertCircle, HelpCircle, CheckCircle2, Gauge } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { StatusBadge } from "../components/badges";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

function WorkloadDialog({ existing, onSaved }) {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    available_capacity_percent: existing?.available_capacity_percent ?? 80,
    meeting_load: existing?.meeting_load || "MEDIUM",
    production_support_percent: existing?.production_support_percent ?? 0,
    urgent_non_project_work: existing?.urgent_non_project_work || "",
    context_switch_concern: existing?.context_switch_concern || false,
    general_help_level: existing?.general_help_level || "NO",
    notes: existing?.notes || "",
  });
  const submit = async () => {
    try {
      await api.post("/me/workload-check", {
        ...form,
        available_capacity_percent: Number(form.available_capacity_percent),
        production_support_percent: Number(form.production_support_percent),
        urgent_non_project_work: form.urgent_non_project_work || null,
        notes: form.notes || null,
      });
      toast.success(t("workloadDone"));
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error(apiError(e));
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button data-testid="workload-check-open-btn" variant={existing ? "outline" : "default"}
          className={existing ? "" : "bg-[#1E3A8A] hover:bg-[#1c3479]"}>
          {existing ? t("workloadDone") + " ✓" : t("workloadCheck")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">{t("workloadCheck")}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>{t("availableCapacity")}</Label>
            <Input data-testid="workload-capacity-input" type="number" min={0} max={100}
              value={form.available_capacity_percent}
              onChange={(e) => setForm({ ...form, available_capacity_percent: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t("meetingLoad")}</Label>
            <Select value={form.meeting_load} onValueChange={(v) => setForm({ ...form, meeting_load: v })}>
              <SelectTrigger data-testid="workload-meeting-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="LOW">Low</SelectItem>
                <SelectItem value="MEDIUM">Medium</SelectItem>
                <SelectItem value="HIGH">High</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t("prodSupport")}</Label>
            <Input type="number" min={0} max={100} value={form.production_support_percent}
              onChange={(e) => setForm({ ...form, production_support_percent: e.target.value })} />
          </div>
          <div className="space-y-1">
            <Label>{t("urgentWork")}</Label>
            <Input value={form.urgent_non_project_work}
              onChange={(e) => setForm({ ...form, urgent_non_project_work: e.target.value })} />
          </div>
          <div className="flex items-center gap-2">
            <Checkbox data-testid="workload-context-switch-checkbox" checked={form.context_switch_concern}
              onCheckedChange={(v) => setForm({ ...form, context_switch_concern: !!v })} id="ctxsw" />
            <Label htmlFor="ctxsw">{t("contextSwitch")}</Label>
          </div>
          <div className="space-y-1">
            <Label>{t("generalHelp")}</Label>
            <Select value={form.general_help_level} onValueChange={(v) => setForm({ ...form, general_help_level: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="NO">{t("helpNo")}</SelectItem>
                <SelectItem value="YES_SOON">{t("helpSoon")}</SelectItem>
                <SelectItem value="YES_NOW">{t("helpNow")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>{t("notes")}</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
          <Button data-testid="workload-submit-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" onClick={submit}>
            {t("submitWorkload")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function MyDaily() {
  const { t } = useLang();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [responses, setResponses] = useState({});

  const load = useCallback(() => api.get("/me/update-queue").then((r) => setData(r.data)).catch((e) => toast.error(apiError(e))), []);
  useEffect(() => { load(); }, [load]);

  if (!data)
    return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-40 w-full" /></div>;

  const respond = async (cid) => {
    const text = responses[cid];
    if (!text) return;
    try {
      await api.post(`/clarifications/${cid}/respond`, { response: text });
      toast.success(t("respond") + " ✓");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="my-daily-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight">{t("myDaily")}</h1>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
            <CalendarDays className="h-4 w-4" />
            {data.reporting_date}
            <span className="mx-1">·</span>
            <span data-testid="my-daily-progress">{t("progressOf")(data.progress.submitted, data.progress.total)}</span>
          </div>
        </div>
        <WorkloadDialog existing={data.workload_check} onSaved={load} />
      </div>

      {data.clarifications.length > 0 && (
        <div className="apha-card border-[#C7D2FE] bg-[#E0E7FF]/40 p-4 space-y-3" data-testid="clarifications-panel">
          <div className="flex items-center gap-2 font-medium text-sm text-[#3730A3]">
            <HelpCircle className="h-4 w-4" /> {t("clarificationsTitle")}
          </div>
          {data.clarifications.map((c) => (
            <div key={c.id} className="bg-white rounded-md border p-3">
              <div className="text-sm">{c.question}</div>
              <div className="flex gap-2 mt-2">
                <Input data-testid="clarification-respond-input" placeholder={t("yourResponse")}
                  value={responses[c.id] || ""} onChange={(e) => setResponses({ ...responses, [c.id]: e.target.value })} />
                <Button data-testid="clarification-respond-btn" size="sm" className="bg-[#1E3A8A] hover:bg-[#1c3479]"
                  onClick={() => respond(c.id)}>{t("respond")}</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
        {data.cards.map((card) => (
          <div key={card.project.id} className="apha-card apha-hover p-5 flex flex-col gap-3" data-testid={`mydaily-card-${card.project.key}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="text-xs text-muted-foreground font-medium">{card.project.key}</div>
                <div className="font-heading font-semibold">{card.project.name}</div>
              </div>
              <StatusBadge status={card.status} testId={`card-status-${card.project.key}`} />
            </div>
            <div className="text-xs text-muted-foreground space-y-1">
              <div>{t("expectedBecause")}: <span className="text-foreground">{card.expected_report.expectation_reason}</span></div>
              <div>{t("deadline")}: {new Date(card.deadline).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div>
              {card.project_health && (
                <div className="flex items-center gap-1.5">
                  <Gauge className="h-3.5 w-3.5" />
                  <StatusBadge status={card.project_health.status} testId={`card-health-${card.project.key}`} />
                  <span>{card.project_health.score}</span>
                </div>
              )}
            </div>
            {card.update?.blocker_flag && (
              <div className="flex items-center gap-1.5 text-xs text-[#9A3412]">
                <AlertCircle className="h-3.5 w-3.5" /> {t("blockerFlag")}: {card.update.blocker_details}
              </div>
            )}
            {card.suggested_issues.length > 0 && (
              <div className="text-xs text-muted-foreground">
                {t("suggestedIssues")}: {card.suggested_issues.slice(0, 3).map((i) => i.issue_key).join(", ")}
              </div>
            )}
            <div className="mt-auto pt-2">
              <Button
                data-testid={`start-update-btn-${card.project.key}`}
                className={`w-full ${card.status.startsWith("SUBMITTED") ? "" : "bg-[#1E3A8A] hover:bg-[#1c3479]"}`}
                variant={card.status.startsWith("SUBMITTED") ? "outline" : "default"}
                onClick={() => navigate(`/update/${card.project.id}`)}
              >
                {card.status.startsWith("SUBMITTED") ? (
                  <span className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4" /> {t("viewUpdate")}</span>
                ) : card.status === "DRAFT" ? t("continueDraft") : t("completeUpdate")}
              </Button>
            </div>
          </div>
        ))}
        {data.cards.length === 0 && (
          <div className="apha-card p-8 text-muted-foreground text-sm col-span-full">{t("noData")}</div>
        )}
      </div>
    </div>
  );
}
